import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from "../../styles/components/FinalScoutModal.module.css";
import { ReactComponent as CrossIcon } from "../../assets/icons/cross.svg";
import { ReactComponent as CreditsIcon } from "../../assets/icons/credits.svg";
import { ReactComponent as AddIcon } from "../../assets/icons/add.svg";
import Avatar from "../../ui/avatar/Avatar";
import { ReactComponent as GlassIcon } from "../../assets/icons/glass.svg";
import { validationService } from "../../services/validation.service";
import { describeRequestError } from "../../utils/requestErrorMessage";
import { useAuth } from "../../contexts/AuthContext";
import { useOverlayClose } from "../../hooks/useOverlayClose";
import {
  getSelectableAccounts,
  resolveSelectableAccountId,
} from "../../utils/accountOptions";
import {
  getBulkSearchStatusLabel,
  isBulkSearchFinished,
  isBulkSearchSuccessful,
} from "../../utils/bulkSearchStatus";

const SECTIONS = {
  EMAIL_SIMPLE: "email_simple",
  EMAIL_MASIVO: "email_masivo",
  LINKEDIN_SIMPLE: "linkedin_simple",
  LINKEDIN_MASIVO: "linkedin_masivo",
};

const ACCOUNT_NAME_MAX_LENGTH = 26;

function getPreviousTaskStatusClass(status) {
  if (isBulkSearchSuccessful(status)) return styles.prevTaskStatusDone;
  if (isBulkSearchFinished(status)) return styles.prevTaskStatusFailed;
  return styles.prevTaskStatusRunning;
}

function truncateAccountName(name) {
  const accountName = name ?? "";
  if (accountName.length <= ACCOUNT_NAME_MAX_LENGTH) return accountName;
  return `${accountName.slice(0, ACCOUNT_NAME_MAX_LENGTH - 1).trimEnd()}…`;
}

function CreditBadge({ text }) {
  return (
    <div className={styles.creditBadge}>
      <CreditsIcon className={styles.creditBadgeIconSvg} />
      <span className={styles.creditBadgeText}>{text}</span>
    </div>
  );
}

function ErrorMessage({ text }) {
  return (
    <div style={{ color: "var(--text-danger, #e53e3e)", fontSize: 13, marginTop: 8 }}>
      {text}
    </div>
  );
}

function EmailSimpleContent({ accountId, onSaved, onCancel }) {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [domain, setDomain] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  async function handleSearch() {
    if (!firstName.trim() || !lastName.trim() || !domain.trim()) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const data = await validationService.searchEmail({
        accountId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        domain: domain.trim(),
      });
      setResult(data);
    } catch (err) {
      if (err.response?.status === 402) {
        setError("Sin créditos disponibles para realizar la búsqueda.");
      } else if (err.response?.status === 403) {
        setError(describeRequestError(err, "No se pudo buscar"));
      } else if (err.response?.status === 502) {
        setError("Error al verificar. Intentá de nuevo.");
      } else {
        setError("Ocurrió un error inesperado. Intentá de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  }

  function handleSave() {
    onSaved?.(null);
  }

  return (
    <>
      <div className={styles.contentHeader}>
        <div className={styles.contentTitle}>
          Búsqueda Simple de Email Profesional
        </div>
        <div className={styles.contentSubtitle}>
          Encontrá mail de trabajo usando nombre y dominio de compañía
        </div>
        <CreditBadge text="2 créditos por mail" />
      </div>

      <div className={styles.simpleForm}>
        <div className={styles.simpleFormRow}>
          <div className={styles.simpleField}>
            <label className={styles.simpleLabel}>Nombre</label>
            <input
              className={styles.simpleInput}
              value={firstName}
              onChange={(event) => setFirstName(event.target.value)}
              placeholder="Nombre"
              onKeyDown={(event) => event.key === "Enter" && handleSearch()}
            />
          </div>

          <div className={styles.simpleField}>
            <label className={styles.simpleLabel}>Apellido</label>
            <input
              className={styles.simpleInput}
              value={lastName}
              onChange={(event) => setLastName(event.target.value)}
              placeholder="Apellido"
              onKeyDown={(event) => event.key === "Enter" && handleSearch()}
            />
          </div>
        </div>

        <div className={styles.simpleFormRow}>
          <div className={styles.simpleField}>
            <label className={styles.simpleLabel}>Dominio de Compañía</label>
            <div className={styles.domainRow}>
              <span className={styles.domainAt}>@</span>
              <input
                className={styles.domainInput}
                value={domain}
                onChange={(event) => setDomain(event.target.value)}
                placeholder="dominio.com"
                onKeyDown={(event) => event.key === "Enter" && handleSearch()}
              />
            </div>
          </div>

          <button
            className={styles.searchBtn}
            onClick={handleSearch}
            disabled={loading || !firstName.trim() || !lastName.trim() || !domain.trim()}
          >
            <GlassIcon className={styles.searchBtnIcon} />
            Buscar
          </button>
        </div>
      </div>

      {loading && (
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} />
          <div>
            <div className={styles.loadingText}>Cargando datos</div>
            <div className={styles.loadingSubtext}>
              Esto puede tardar hasta 90 segundos
            </div>
          </div>
        </div>
      )}

      {error && !loading && <ErrorMessage text={error} />}

      {result && !loading && result.found && (
        <div className={styles.resultCard}>
          <div className={styles.resultCardHeader}>
            <Avatar
              src={result.photo ?? result.photo_url ?? null}
              name={`${result.first_name ?? firstName} ${result.last_name ?? lastName}`}
              size={44}
            />
            <span className={styles.resultCardName}>
              {result.full_name ?? `${firstName} ${lastName}`}
            </span>
          </div>

          <div className={styles.resultFields}>
            <div className={styles.resultField}>
              <span className={styles.resultFieldLabel}>Nombre</span>
              <div className={styles.resultFieldValue}>{result.first_name}</div>
            </div>
            <div className={styles.resultField}>
              <span className={styles.resultFieldLabel}>Apellido</span>
              <div className={styles.resultFieldValue}>{result.last_name}</div>
            </div>
            <div className={styles.resultField}>
              <span className={styles.resultFieldLabel}>Email</span>
              <div className={styles.resultFieldValue}>{result.email}</div>
            </div>
            <div className={styles.resultField}>
              <span className={styles.resultFieldLabel}>Estado</span>
              <div className={styles.resultFieldValue}>{result.email_status}</div>
            </div>
          </div>
        </div>
      )}

      {result && !loading && !result.found && (
        <div className={styles.notFoundState}>
          <div className={styles.notFoundTitle}>No se encontró la persona</div>
          <div>No se encontraron datos para esta búsqueda.</div>
        </div>
      )}

      {result && (
        <div className={styles.contentActions}>
          <button className={styles.cancelBtn} onClick={onCancel}>
            Cancelar
          </button>
          {result.found && (
            <button className={styles.saveContactBtn} onClick={handleSave}>
              Guardar contacto
            </button>
          )}
        </div>
      )}
    </>
  );
}

function PreviousTasksPanel({ accountId }) {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  async function load() {
    if (loading) return;
    setLoading(true);
    try {
      const data = await validationService.listBulkTasks(accountId);
      setTasks(data);
    } catch (err) {
      console.error("Error cargando búsquedas previas:", err);
    } finally {
      setLoading(false);
    }
  }

  function toggle() {
    if (!open) load();
    setOpen((isOpen) => !isOpen);
  }

  function formatDate(iso) {
    const d = new Date(iso);
    return d.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  }

  return (
    <div className={styles.prevTasksWrap}>
      <button className={styles.prevTasksToggle} onClick={toggle} type="button">
        Búsquedas previas
        <svg
          className={`${styles.prevTasksChevron} ${open ? styles.prevTasksChevronOpen : ""}`}
          viewBox="0 0 14 14" fill="none" width={12} height={12}
        >
          <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className={styles.prevTasksList}>
          {loading && <div className={styles.prevTasksEmpty}>Cargando...</div>}
          {!loading && tasks.length === 0 && (
            <div className={styles.prevTasksEmpty}>No hay búsquedas previas.</div>
          )}
          {!loading && tasks.map((task) => (
            <button
              key={task.id}
              className={styles.prevTaskItem}
              onClick={() => navigate(`/database/bulk/${task.id}`)}
              type="button"
            >
              <div className={styles.prevTaskInfo}>
                <span className={styles.prevTaskDate}>{formatDate(task.created_at)}</span>
                <span className={styles.prevTaskCount}>{task.finished}/{task.total} encontrados</span>
              </div>
              <span className={`${styles.prevTaskStatus} ${getPreviousTaskStatusClass(task.status)}`}>
                {getBulkSearchStatusLabel(task.status)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function BulkContent({ accountId, onCancel }) {
  const navigate = useNavigate();
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  function handleFile(f) {
    if (!f) return;
    setFile(f);
    setError(null);
  }

  function formatBytes(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  async function handleSearch() {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const data = await validationService.searchBulk({ accountId, file });
      navigate(`/database/bulk/${data.task_id}`);
    } catch (err) {
      if (err.response?.status === 402) {
        setError("Créditos insuficientes para procesar todas las filas del CSV.");
      } else if (err.response?.status === 403) {
        setError(describeRequestError(err, "No se pudo iniciar la búsqueda"));
      } else if (err.response?.status === 400) {
        setError(
          err.response?.data?.detail ??
            "El archivo CSV es inválido. Revisá el formato y volvé a intentar."
        );
      } else {
        setError("Ocurrió un error inesperado. Intentá de nuevo.");
      }
      setLoading(false);
    }
  }

  async function handleDownloadTemplate() {
    try {
      await validationService.downloadTemplate();
    } catch (err) {
      console.error("Error al descargar el template:", err);
    }
  }

  return (
    <>
      {!file ? (
        <div
          className={`${styles.uploadDropzone} ${dragging ? styles.uploadDropzoneActive : ""}`}
          onDragOver={(event) => { event.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            handleFile(event.dataTransfer.files[0]);
          }}
          onClick={() => fileRef.current?.click()}
        >
          <AddIcon className={styles.uploadIcon} />
          <input
            ref={fileRef}
            type="file"
            accept=".csv"
            className={styles.uploadHiddenInput}
            onChange={(event) => handleFile(event.target.files[0])}
            onClick={(event) => event.stopPropagation()}
          />
        </div>
      ) : (
        <div className={styles.uploadFileRow}>
          <span className={styles.uploadFileName}>{file.name}</span>
          <span className={styles.uploadFileSize}>({formatBytes(file.size)})</span>
          <button
            className={styles.uploadClearBtn}
            onClick={() => { setFile(null); setError(null); }}
          >
            <CrossIcon className={styles.uploadClearIcon} />
          </button>
        </div>
      )}

      <div className={styles.bulkMeta}>
        <button
          className={styles.templateLink}
          onClick={handleDownloadTemplate}
          type="button"
        >
          Descargar template de .CSV
        </button>
        <PreviousTasksPanel accountId={accountId} />
      </div>

      {error && !loading && <ErrorMessage text={error} />}

      {loading && (
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} />
          <div>
            <div className={styles.loadingText}>Subiendo archivo</div>
            <div className={styles.loadingSubtext}>
              Iniciando proceso de búsqueda...
            </div>
          </div>
        </div>
      )}

      {file && !loading && (
        <button
          className={styles.searchBtn}
          style={{ marginTop: 16, alignSelf: "flex-end" }}
          onClick={handleSearch}
        >
          <GlassIcon className={styles.searchBtnIcon} />
          Buscar
        </button>
      )}

      {!loading && (
        <div className={styles.contentActions}>
          <button className={styles.cancelBtn} onClick={onCancel}>
            Cancelar
          </button>
        </div>
      )}
    </>
  );
}

function EmailMasivoContent({ accountId, onCancel }) {
  return (
    <>
      <div className={styles.contentHeader}>
        <div className={styles.contentTitle}>
          Búsqueda Masiva de Email Profesional
        </div>
        <div className={styles.contentSubtitle}>
          Encontrá mails de trabajo de forma masiva adjuntando un .CSV acorde al
          template.
        </div>
        <CreditBadge text="2 créditos por mail encontrado en el .CSV" />
      </div>
      <BulkContent accountId={accountId} onCancel={onCancel} />
    </>
  );
}

function LinkedinSimpleContent({ accountId, onSaved, onCancel }) {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  async function handleSearch() {
    if (!url.trim()) return;
    setLoading(true);
    setResult(null);
    setError(null);
    try {
      const data = await validationService.searchLinkedin({
        accountId,
        linkedinUrl: url.trim(),
      });
      setResult(data);
    } catch (err) {
      if (err.response?.status === 402) {
        setError("Sin créditos disponibles para realizar la búsqueda.");
      } else if (err.response?.status === 403) {
        setError(describeRequestError(err, "No se pudo buscar"));
      } else if (err.response?.status === 502) {
        setError("Error al verificar. Intentá de nuevo.");
      } else {
        setError("Ocurrió un error inesperado. Intentá de nuevo.");
      }
    } finally {
      setLoading(false);
    }
  }

  function handleSave() {
    onSaved?.(null);
  }

  return (
    <>
      <div className={styles.contentHeader}>
        <div className={styles.contentTitle}>Búsqueda Simple de LinkedIn</div>
        <div className={styles.contentSubtitle}>
          Encontrá mail de trabajo usando link de LinkedIn
        </div>
        <CreditBadge text="2 créditos por mail" />
      </div>

      <div className={styles.simpleForm}>
        <div className={styles.simpleField}>
          <label className={styles.simpleLabel}>Link de Usuario en LinkedIn</label>
          <input
            className={styles.simpleInput}
            value={url}
            onChange={(event) => setUrl(event.target.value)}
            placeholder="https://www.linkedin.com/in/usuario"
            type="url"
            onKeyDown={(event) => event.key === "Enter" && handleSearch()}
          />
        </div>

        <button
          className={styles.searchBtn}
          onClick={handleSearch}
          disabled={loading || !url.trim()}
          style={{ marginTop: "auto" }}
        >
          <GlassIcon className={styles.searchBtnIcon} />
          Buscar
        </button>
      </div>

      {loading && (
        <div className={styles.loadingState}>
          <div className={styles.loadingSpinner} />
          <div>
            <div className={styles.loadingText}>Cargando datos</div>
            <div className={styles.loadingSubtext}>
              Esto puede tardar hasta 90 segundos
            </div>
          </div>
        </div>
      )}

      {error && !loading && <ErrorMessage text={error} />}

      {result && !loading && result.found && (
        <div className={styles.resultCard}>
          <div className={styles.resultCardHeader}>
            <Avatar
              src={result.photo ?? result.photo_url ?? null}
              name={`${result.first_name} ${result.last_name}`}
              size={44}
            />
            <span className={styles.resultCardName}>
              {result.full_name ?? `${result.first_name} ${result.last_name}`}
            </span>
          </div>
          <div className={styles.resultFields}>
            <div className={styles.resultField}>
              <span className={styles.resultFieldLabel}>Nombre</span>
              <div className={styles.resultFieldValue}>{result.first_name}</div>
            </div>
            <div className={styles.resultField}>
              <span className={styles.resultFieldLabel}>Apellido</span>
              <div className={styles.resultFieldValue}>{result.last_name}</div>
            </div>
            <div className={styles.resultField}>
              <span className={styles.resultFieldLabel}>Email</span>
              <div className={styles.resultFieldValue}>{result.email}</div>
            </div>
            <div className={styles.resultField}>
              <span className={styles.resultFieldLabel}>Estado</span>
              <div className={styles.resultFieldValue}>{result.email_status}</div>
            </div>
          </div>
        </div>
      )}

      {result && !loading && !result.found && (
        <div className={styles.notFoundState}>
          <div className={styles.notFoundTitle}>No se encontró la persona</div>
          <div>No se encontraron datos para esta búsqueda.</div>
        </div>
      )}

      {result && (
        <div className={styles.contentActions}>
          <button className={styles.cancelBtn} onClick={onCancel}>Cancelar</button>
          {result.found && (
            <button className={styles.saveContactBtn} onClick={handleSave}>
              Guardar contacto
            </button>
          )}
        </div>
      )}
    </>
  );
}

function LinkedinMasivoContent({ accountId, onCancel }) {
  return (
    <>
      <div className={styles.contentHeader}>
        <div className={styles.contentTitle}>
          Búsqueda Masiva de LinkedIn
        </div>
        <div className={styles.contentSubtitle}>
          Encontrá mails de trabajo de forma masiva adjuntando un .CSV con URLs
          de LinkedIn.
        </div>
        <CreditBadge text="2 créditos por mail encontrado en el .CSV" />
      </div>
      <BulkContent accountId={accountId} onCancel={onCancel} />
    </>
  );
}

export default function FinalScoutModal({ onClose, onContactSaved }) {
  const { activeAccountId, accounts } = useAuth();
  const [activeSection, setActiveSection] = useState(SECTIONS.EMAIL_SIMPLE);
  const [creditsData, setCreditsData] = useState(null);
  const [selectedAccountId, setSelectedAccountId] = useState(null);

  const accountOptions = getSelectableAccounts(accounts);
  const searchAccountId =
    selectedAccountId ??
    resolveSelectableAccountId(accounts, activeAccountId) ??
    activeAccountId ??
    accountOptions[0]?.id ??
    null;

  useEffect(() => {
    if (!searchAccountId) return;
    setCreditsData(null);
    validationService
      .getCredits(searchAccountId)
      .then(setCreditsData)
      .catch(() => {});
  }, [searchAccountId]);

  const overlayCloseProps = useOverlayClose(onClose);

  function handleContactSaved(contact) {
    onContactSaved?.(contact);
    onClose();
  }

  function handleBulkSaved() {
    onContactSaved?.(null);
    onClose();
  }

  const creditsAvailable = creditsData?.available ?? 0;
  const creditsLimit = creditsData?.limit ?? 1;
  const isUnlimited = creditsData?.is_unlimited ?? false;
  const creditsPercent = isUnlimited
    ? 100
    : Math.min(100, (creditsAvailable / creditsLimit) * 100);
  const creditsDisplay = isUnlimited
    ? "Ilimitados"
    : creditsAvailable.toLocaleString();

  const navItems = [
    { section: SECTIONS.EMAIL_SIMPLE, label: "Búsqueda Simple", category: "EMAIL PROFESIONAL" },
    { section: SECTIONS.EMAIL_MASIVO, label: "Búsqueda Masiva", category: null },
    { section: SECTIONS.LINKEDIN_SIMPLE, label: "Búsqueda Simple", category: "EMAIL DE LINKEDIN" },
    { section: SECTIONS.LINKEDIN_MASIVO, label: "Búsqueda Masiva", category: null },
  ];

  return (
    <div className={styles.overlay} {...overlayCloseProps}>
      <div className={styles.modal} onClick={(event) => event.stopPropagation()}>
        {/* Sidebar */}
        <div className={styles.sidebar}>
          <div className={styles.sidebarTop}>
            <button
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Cerrar"
            >
              <CrossIcon className={styles.closeBtnIcon} />
            </button>
          </div>

          {accountOptions.length > 1 && (
            <div className={styles.accountSelectSection}>
              <label
                className={styles.accountSelectLabel}
                htmlFor="final-scout-account"
              >
                Cuenta
              </label>
              <div className={styles.accountSelectWrap}>
                <select
                  id="final-scout-account"
                  className={styles.accountSelect}
                  value={searchAccountId ?? ""}
                  onChange={(event) =>
                    setSelectedAccountId(Number(event.target.value))
                  }
                >
                  {accountOptions.map((account) => (
                    <option
                      key={account.id}
                      value={account.id}
                      title={account.name}
                    >
                      {truncateAccountName(account.name)}
                    </option>
                  ))}
                </select>
                <svg
                  className={styles.accountSelectChevron}
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    d="M3 5L7 9L11 5"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          )}

          <div className={styles.sidebarNav}>
            {navItems.map((item) => (
              <div key={item.section}>
                {item.category && (
                  <div className={styles.sidebarCategory}>{item.category}</div>
                )}
                <button
                  className={`${styles.sidebarItem} ${activeSection === item.section ? styles.sidebarItemActive : ""}`}
                  onClick={() => setActiveSection(item.section)}
                >
                  {item.label}
                </button>
              </div>
            ))}
          </div>

          <div className={styles.creditsSection}>
            <div className={styles.creditsDisplay}>
              <CreditsIcon className={styles.creditsIconSvg} />
              <div className={styles.creditsInfo}>
                <div className={styles.creditsNumber}>{creditsDisplay}</div>
                <div className={styles.creditsLabel}>créditos disponibles</div>
              </div>
            </div>
            <div className={styles.creditsBar}>
              <div
                className={styles.creditsBarFill}
                style={{ width: `${creditsPercent}%` }}
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className={styles.content}>
          {activeSection === SECTIONS.EMAIL_SIMPLE && (
            <EmailSimpleContent
              accountId={searchAccountId}
              onSaved={handleContactSaved}
              onCancel={onClose}
            />
          )}
          {activeSection === SECTIONS.EMAIL_MASIVO && (
            <EmailMasivoContent
              accountId={searchAccountId}
              onSaved={handleBulkSaved}
              onCancel={onClose}
            />
          )}
          {activeSection === SECTIONS.LINKEDIN_SIMPLE && (
            <LinkedinSimpleContent
              accountId={searchAccountId}
              onSaved={handleContactSaved}
              onCancel={onClose}
            />
          )}
          {activeSection === SECTIONS.LINKEDIN_MASIVO && (
            <LinkedinMasivoContent
              accountId={searchAccountId}
              onSaved={handleBulkSaved}
              onCancel={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}
